import React, { useState, useEffect, useRef } from 'react';
import {
  Table,
  Tag,
  Button,
  Space,
  message,
  Modal,
  Descriptions,
  Typography,
  Popconfirm,
  Input,
  Rate,
  Card,
  Statistic,
  Row,
  Col,
} from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  EyeOutlined,
  DeleteOutlined,
  SearchOutlined,
  StarOutlined,
  UserOutlined,
  MailOutlined,
} from '@ant-design/icons';
import { testimonialAPI } from '../../utils/api';
import { Testimonial } from '../../types';
import dayjs from 'dayjs';

const { Title,Paragraph } = Typography;

interface TestimonialsManagementProps {
  active: string;
}

const TestimonialsManagement: React.FC<TestimonialsManagementProps> = ({ active }) => {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [detailModalVisible, setDetailModalVisible] = useState<boolean>(false);
  const [selectedTestimonial, setSelectedTestimonial] = useState<Testimonial | null>(null);
  const [searchText, setSearchText] = useState<string>('');
  const [stats, setStats] = useState({
    total: 0,
    approved: 0,
    pending: 0,
    rejected: 0,
    avgRating: 0,
  });
  
  const hasFetchedRef = useRef<boolean>(false);

  useEffect(() => {
    if (active === 'testimonials' && !hasFetchedRef.current) {
      fetchTestimonials();
      fetchStats();
      hasFetchedRef.current = true;
    }
  }, [active]);

  const fetchTestimonials = async () => {
    try {
      setLoading(true);
      const response = await testimonialAPI.getAllAdmin();
      setTestimonials(response.data.testimonials || []);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
      message.error('Failed to load testimonials');
      setTestimonials([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await testimonialAPI.getStats();
      setStats(response.data.stats);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleApprove = async (id: string) => {
    try {
      await testimonialAPI.approve(id);
      message.success('Testimonial approved successfully');
      fetchTestimonials();
      fetchStats();
    } catch (error) {
      console.error('Error approving testimonial:', error);
      message.error('Failed to approve testimonial');
    }
  };

  const handleReject = async (id: string) => {
    try {
      await testimonialAPI.reject(id);
      message.success('Testimonial rejected');
      fetchTestimonials();
      fetchStats();
    } catch (error) {
      console.error('Error rejecting testimonial:', error);
      message.error('Failed to reject testimonial');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await testimonialAPI.delete(id);
      message.success('Testimonial deleted successfully');
      fetchTestimonials();
      fetchStats();
    } catch (error) {
      console.error('Error deleting testimonial:', error);
      message.error('Failed to delete testimonial');
    }
  };

  const showDetails = (testimonial: Testimonial) => {
    setSelectedTestimonial(testimonial);
    setDetailModalVisible(true);
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'pending':
        return 'orange';
      case 'approved':
        return 'green';
      case 'rejected':
        return 'red';
      default:
        return 'default';
    }
  };

  const filteredTestimonials = (testimonials || []).filter(testimonial =>
    testimonial.name?.toLowerCase().includes(searchText.toLowerCase()) ||
    testimonial.email?.toLowerCase().includes(searchText.toLowerCase()) ||
    testimonial.comment?.toLowerCase().includes(searchText.toLowerCase())
  );

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => (
        <Space>
          <UserOutlined />
          <span>{text}</span>
        </Space>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (text: string) => (
        <Space>
          <MailOutlined />
          <span>{text}</span>
        </Space>
      ),
    },
    {
      title: 'Rating',
      dataIndex: 'rating',
      key: 'rating',
      render: (rating: number) => <Rate disabled defaultValue={rating} />,
    },
    {
      title: 'Comment',
      dataIndex: 'comment',
      key: 'comment',
      render: (text: string) => (
        <Paragraph ellipsis={{ rows: 2 }} style={{ marginBottom: 0 }}>
          {text}
        </Paragraph>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {status.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => dayjs(date).format('MMM DD, YYYY'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: Testimonial) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => showDetails(record)}
          >
            View
          </Button>
          {record.status === 'pending' && (
            <>
              <Button
                type="link"
                icon={<CheckOutlined />}
                onClick={() => handleApprove(record._id!)}
                style={{ color: 'green' }}
              >
                Approve
              </Button>
              <Button
                type="link"
                danger
                icon={<CloseOutlined />}
                onClick={() => handleReject(record._id!)}
              >
                Reject
              </Button>
            </>
          )}
          {record.status === 'approved' && (
            <Button
              type="link"
              danger
              icon={<CloseOutlined />}
              onClick={() => handleReject(record._id!)}
            >
              Unapprove
            </Button>
          )}
          <Popconfirm
            title="Are you sure you want to delete this testimonial?"
            onConfirm={() => handleDelete(record._id!)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={3} style={{ margin: 0 }}>Testimonials Management</Title>
      </div>

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Testimonials"
              value={stats.total}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Approved"
              value={stats.approved}
              valueStyle={{ color: '#3f8600' }}
              prefix={<CheckOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Pending Review"
              value={stats.pending}
              valueStyle={{ color: '#faad14' }}
              prefix={<CloseOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Average Rating"
              value={stats.avgRating}
              precision={1}
              valueStyle={{ color: '#1890ff' }}
              prefix={<StarOutlined />}
              suffix="/ 5"
            />
          </Card>
        </Col>
      </Row>

      {/* Search */}
      <Input
        placeholder="Search by name, email, or comment"
        prefix={<SearchOutlined />}
        style={{ width: '100%', maxWidth: 400, marginBottom: 16 }}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
      />

      {/* Table */}
      <Table
        columns={columns}
        dataSource={filteredTestimonials}
        rowKey="_id"
        loading={loading}
        pagination={{ pageSize: 10 }}
        scroll={{ x: 1200 }}
      />

      {/* Detail Modal */}
      <Modal
        title="Testimonial Details"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            Close
          </Button>,
        ]}
        width="95%"
        style={{ maxWidth: 700 }}
      >
        {selectedTestimonial && (
          <Descriptions
            bordered
            column={{ xs: 1, sm: 1, md: 2 }}
          >
            <Descriptions.Item label="Name" span={2}>
              {selectedTestimonial.name}
            </Descriptions.Item>
            <Descriptions.Item label="Email">
              {selectedTestimonial.email}
            </Descriptions.Item>
            <Descriptions.Item label="Role">
              {selectedTestimonial.role}
            </Descriptions.Item>
            <Descriptions.Item label="Rating" span={2}>
              <Rate disabled defaultValue={selectedTestimonial.rating} />
            </Descriptions.Item>
            <Descriptions.Item label="Comment" span={2}>
              {selectedTestimonial.comment}
            </Descriptions.Item>
            {selectedTestimonial.tourPackage && (
              <Descriptions.Item label="Tour Package" span={2}>
                {selectedTestimonial.tourPackage}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Status">
              <Tag color={getStatusColor(selectedTestimonial.status)}>
                {selectedTestimonial.status.toUpperCase()}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Submitted At">
              {dayjs(selectedTestimonial.createdAt).format('MMMM DD, YYYY HH:mm')}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default TestimonialsManagement;
